import cx from "classnames";
import { useRef, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Menu,
  Modal,
  ToolSelect,
  Input,
  List,
  Flex
} from "@theorgchartdev/storybook";
import { ChartBuilderRuleInput } from "./ChartBuilderRuleInput/ChartBuilderRuleInput";
import BasicChartSearchMenu from "../../Menus/BasicChartSearchMenu/BasicChartSearchMenu";
import EditableItemGroup from "../../Utils/EditableItemGroup/EditableItemGroup";
import SectionPrompt from "../../Composites/SectionPrompt/SectionPrompt";
import { PlanFieldsModal } from "@/appComponents/Blocks/Modals/PlanFieldsModal";
import { GetFieldDefsFromList } from "@/Utils/DataLib";
import ToolbarPopover from "@/appComponents/Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumObjectTypes, kDefaultPlanFields } from "@coreapp/constants";
import { FormatEmployeeDictLabel } from "@coreapp/utils/FormatEmployeeDictLabel";
import { convertStdDateString } from "@coreapp/utils/convertStdDateString";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";

const GetLevelString = levelValue => {
  if (!Globals._stringMgr) return levelValue;

  if (levelValue === -1 || levelValue == null) return appstr("alllevels");

  return levelValue.toString() + " " + (levelValue === 1 ? appstr("Level") : appstr("Levels"));
};

const BuildLevelOptions = onLevelChange => {
  let levelOptions = [];
  levelOptions.push({
    label: GetLevelString(-1),
    value: -1,
    clickHandler: onLevelChange
  });
  for (let i = 2; i < 11; i++) {
    levelOptions.push({
      label: GetLevelString(i),
      value: i,
      clickHandler: onLevelChange
    });
  }
  return levelOptions;
};

const GetDefaultFieldNames = () => {
  let defaultFieldDefs = GetFieldDefsFromList(kDefaultPlanFields);
  return defaultFieldDefs.map(item => {
    return item.GetFieldLabel() ? item.GetFieldLabel() : item.GetFieldName();
  });
};

const ChartBuilderModal = ({
  show,
  onClose,
  onCreate,
  isPlan = false,
  pitBuilderInfo = null,
  autoPopulateTopbox = true,
  hasFilterError = false,
  queryID = null,
  initialSettings = {}
}) => {
  // scm get top of chart and format emp label

  const [levels, setLevels] = useState(-1);
  const [targetDBObj, setTargetDBObj] = useState(initialSettings?.targetDataObj);
  const [topboxID, setTargetBoxID] = useState(-1);
  const [searchLabel, setSearchLabel] = useState(appstr("No record selected"));
  const [showSearch, setShowSearch] = useState(false);
  const [filterRule, setFilterRule] = useState(null);
  const initialFilterRuleRef = useRef(null);
  const [nameValue, setNameValue] = useState("");
  const [inputError, setInputError] = useState({
    status: "default",
    message: appstr("Plan Name cannot be left blank")
  });
  const [fields, setFields] = useState(GetDefaultFieldNames() || []);
  const [additionalFields, setAdditonalFields] = useState([]);
  const [planAdditionalFields, setPlanAdditionalFields] = useState([]);
  const [showFieldEditor, setShowFieldEditor] = useState(false);

  if (!show) {
    return null;
  }
  let isArchive = false;
  if (pitBuilderInfo) isArchive = true;

  const SetBuildTarget = dbObj => {
    setTargetDBObj(dbObj);
    setTargetBoxID(dbObj.GetObjectRecordID());
    setSearchLabel(dbObj.FormatEmployeeLabel());
  };

  const GetTopOfChart = () => {
    let scm = StateLib.GetSubChartMgr();
    if (!scm || !scm.GetCurrentSubChart()) return null;
    let topDBObj = scm.GetCurrentTopObj();
    if (topDBObj && autoPopulateTopbox) {
      SetBuildTarget(topDBObj);
    }
  };

  if (!targetDBObj && topboxID === -1) {
    if (initialSettings?.targetDataObj instanceof QObjDBObject) {
      SetBuildTarget(initialSettings.targetDataObj);
    } else {
      GetTopOfChart();
    }
  }

  let disableBuild = topboxID === -1;
  let recIDFieldName = pitBuilderInfo?.fields["PersonID"];

  const OnLevelChange = target => {
    var numLevels = parseInt(target.value);
    setLevels(numLevels);
  };

  const handleSearchOptionClick = target => {
    let doc = StateLib.GetDoc();
    const mapping = doc.GetFieldMap(enumObjectTypes.holder);
    let formatedName = FormatEmployeeDictLabel(target.result, mapping);
    setTargetBoxID(target.value);
    setSearchLabel(formatedName);
    setShowSearch(false);
  };

  const GetBuilderInfo = () => {
    let builderInfo = {};
    let doc = StateLib.GetDoc();
    let activeViewID = doc.GetActiveTemplate().GetTemplateID();
    builderInfo["liveChart"] = false;
    builderInfo["levels"] = levels;
    builderInfo["topBoxID"] = topboxID;
    builderInfo["importFilter"] = {
      rule: filterRule
    };
    builderInfo["viewid"] = activeViewID;
    if (isPlan) {
      builderInfo["name"] = nameValue;
      builderInfo["fields"] = additionalFields;
    }

    if (pitBuilderInfo) {
      builderInfo["fBuildArchive"] = true;
      builderInfo["doctype"] = "Archive";
    }

    return builderInfo;
  };

  const setErrorStatus = status => {
    setInputError(prev => ({ ...prev, status: status }));
  };

  const clearStoredFilter = () => {
    initialFilterRuleRef.current = null;
  };

  const storeCurrentFilter = () => {
    initialFilterRuleRef.current = filterRule?.Clone();
  };

  const ResetTopBox = () => {
    setTargetDBObj(null);
    setTargetBoxID(-1);
  };

  const handleClose = () => {
    setFilterRule(initialFilterRuleRef.current?.Clone());
    onClose();
    ResetTopBox();
  };

  const handleCreate = () => {
    setErrorStatus("default");
    if (isPlan && nameValue === "") {
      setErrorStatus("error");
      return false;
    }

    storeCurrentFilter();
    onCreate(GetBuilderInfo());
    ResetTopBox();
  };

  const toggleShowSearch = () => {
    setShowSearch(!showSearch);
  };

  const handleOnClickManageEditableFields = () => {
    setShowFieldEditor(true);
    setPlanAdditionalFields(additionalFields);
  };
  const handleOnClosePlanFieldsModal = () => {
    setShowFieldEditor(false);
    setPlanAdditionalFields([]);
  };
  const handleOnChangePlanFieldsModal = fields => setPlanAdditionalFields(fields);
  const handleOnSavePlanFieldsModal = () => setAdditonalFields(planAdditionalFields);

  let levelOptions = BuildLevelOptions(OnLevelChange);

  const baseClass = "ocn-builder";
  const levelSelectTrigger = ({ ariaAttrs }) => (
    <ToolSelect
      disabled={false}
      label={GetLevelString(levels)}
      showArrow
      showLabelOverflow={false}
      size="small"
      sizeProps={{
        maxWidth: 200,
        minWidth: 50
      }}
      {...ariaAttrs}
    />
  );

  const GetFieldsDisplayList = fields => {
    const fieldsDisplay = fields.map(field => {
      return { label: field, value: field };
    });

    return fieldsDisplay.slice(0, 3);
  };

  const allFieldsList = [...fields, ...additionalFields];

  const fieldsPopoverTrigger = (
    <Flex padding={"0px 8px 0px"}>
      <Button modifier={"link"}>{`+${allFieldsList.length - 3} more`}</Button>
    </Flex>
  );

  const fieldsPopover = (
    <ToolbarPopover
      placement={"top"}
      otherPopoverProps={{ reference: fieldsPopoverTrigger }}
      overlayProps={{
        className: cx("oc-dynamic-list-size", "oc-overlay-relative"),
        cardProps: { className: cx("ocn-list-scrollbar", "oc-overflow-auto", "oc-fill-height") }
      }}
    >
      <List
        options={allFieldsList.map(field => {
          return { label: `\u2022 ${field}`, value: field };
        })}
        optionSize={"small"}
        enableVirtualization={false}
      />
    </ToolbarPopover>
  );

  const wsMgr = StateLib.GetWSMgr();
  let headerStr = appstr("Create custom chart");
  if (pitBuilderInfo && wsMgr) {
    const oi = wsMgr.GetOrgInfo();
    const DBHotDate = wsMgr.GetPointInTime();
    const splitResult = DBHotDate["hotdate"].split(":");
    const dateObj = convertStdDateString(splitResult[0], splitResult[1] ?? null);

    const dateLabel = dateObj.FormatDate(oi.GetDateFormat());
    headerStr = `${appstr("Chart Builder")} - ${appstr("Snapshot")} | ${dateLabel}`;
  }

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="builder-card"
        show={show}
        centerScreen={true}
        onClose={handleClose}
        fullWidth={true}
        customStyle={{ padding: 0, width: "600px" }}
      >
        <DialogTitle divider={true} onClose={handleClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={isPlan ? "planning" : "diagram-project"} />
            {headerStr}
          </span>
        </DialogTitle>
        {isPlan && (
          <DialogContent dividers={true}>
            <span className={baseClass}>
              <SectionPrompt
                title={appstr("What would you like to call this plan?")}
                titleId="builder-name-title"
                description={appstr("Choose Plan Name")}
              >
                <Input
                  id={"builder-name-input"}
                  aria-labelledby="builder-name-title"
                  key={"name-plan-input"}
                  placeholder={appstr("Enter Plan Name")}
                  value={nameValue}
                  size={"small"}
                  status={inputError.status}
                  message={inputError.message}
                  onChange={e => setNameValue(e.target.value)}
                />
              </SectionPrompt>
            </span>
          </DialogContent>
        )}
        <DialogContent dividers={true}>
          <span className={baseClass}>
            <SectionPrompt
              title={isPlan ? appstr("Build Employees Plan") : appstr("Build Employees Chart")}
              titleId="builder-topbox-title"
              description={appstr("Build Employees Chart Directions")}
            >
              <div className={"ocn-builder-search"}>
                <span className={"ocn-builder-search-text"}>
                  {!showSearch ? (
                    <EditableItemGroup
                      dataTestId={"builder-srch-txt-btn"}
                      spaceBetween
                      actions={[{ onClick: toggleShowSearch, iconName: "pen" }]}
                    >
                      <span
                        id="builder-topbox-value"
                        aria-labelledby="builder-topbox-title builder-topbox-value"
                        className={"ocn-builder-selectable-label"}
                        tabIndex={0}
                        role="button"
                        onKeyDown={e => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleShowSearch();
                          }
                        }}
                        onClick={toggleShowSearch}
                      >
                        {searchLabel}
                      </span>
                    </EditableItemGroup>
                  ) : (
                    <BasicChartSearchMenu
                      inputProps={{ autoFocus: true, "aria-labelledby": "builder-topbox-title" }}
                      optionSize={"small"}
                      inputSize={"small"}
                      handleSearchOptionClick={handleSearchOptionClick}
                      id={"builder-search"}
                      dataTestId={"builder-search-input"}
                      queryChartId={queryID}
                      recIDFieldName={recIDFieldName}
                      isArchive={isArchive}
                    />
                  )}
                  {showSearch ? (
                    <span className={"ocn-builder-search-button"}>
                      <Button
                        modifier={"secondary"}
                        iconProps={{ name: "xmark" }}
                        onClick={toggleShowSearch}
                        size={"small"}
                        iconOnly={true}
                      />
                    </span>
                  ) : null}
                </span>
              </div>
            </SectionPrompt>
          </span>
        </DialogContent>
        <DialogContent dividers={true}>
          <span className={baseClass}>
            <SectionPrompt
              title={appstr("Select Build Chart Level")}
              titleId="builder-levels-title"
              description={appstr("Select Build Chart Level Directions")}
              divider={true}
            >
              <div className={"ocn-builder-search"}>
                <span className={"ocn-builder-edit-text"}>
                  <Menu
                    dataTestId={"builder-lvls-menu"}
                    ariaLabelledBy="builder-levels-title"
                    optionSize={"small"}
                    trigger={levelSelectTrigger}
                    value={levels}
                    bold={true}
                    options={levelOptions}
                  />
                </span>
              </div>
            </SectionPrompt>
          </span>
        </DialogContent>
        {!isPlan && (
          <DialogContent dividers={true}>
            <span className={baseClass}>
              <SectionPrompt
                title={appstr("Chart Builder Filter")}
                description={appstr("Chart Builder Filter Directions")}
              >
                <div className={"ocn-builder-edit"}>
                  <span id={"builder-filter-btn"} className={"ocn-builder-edit-text"}>
                    <ChartBuilderRuleInput
                      hasError={hasFilterError}
                      onSave={storeCurrentFilter}
                      onDelete={clearStoredFilter}
                      rule={filterRule}
                      setRule={setFilterRule}
                    />
                  </span>
                </div>
              </SectionPrompt>
            </span>
          </DialogContent>
        )}
        <DialogActions>
          <DialogActions.Tertiary onClick={handleClose}>{appstr("Cancel")}</DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={handleCreate}
            dataTestId={"builder-create-btn"}
            disabled={disableBuild}
          >
            {appstr("Create")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default ChartBuilderModal;

/* TODO add old pres options?
 { showPresOptions &&
    <React.Fragment>
        <span className={`${sectionClass}`}>
            <span className={`${sectionClass}-label`}>{"What view would you like to apply?"}</span>
            <Menu label={"Select view"} value={""} options={[]}/>
        </span>
        <span className={`${sectionClass}`}>
            <span className={`${sectionClass}-label`}>{"What fields do you need?"}</span>
            <Menu label={"Select fields"} value={""} options={[]}/>
        </span>
    </React.Fragment>
 */
